import React, { MouseEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  ADJUSTMENT_DATE_KEY,
  ADJUSTMENT_KEY,
  BAD_DEBT_DATE_KEY,
  BAD_DEBT_KEY,
  COMPANY_NAME_KEY,
  CREATED_AT_KEY,
  DEAL_DUE_KEY,
  DEAL_KEY,
  DEAL_NAME_KEY,
  DEAL_STAGE_KEY,
  DUE_KEY,
  ENGAGEMENT_MANAGER_KEY,
  NET_VALUE_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  PAY_1_DATE_KEY,
  PAY_2_DATE_KEY,
  PAY_3_DATE_KEY,
  PAYMENT_1_KEY,
  PAYMENT_2_KEY,
  PAYMENT_3_KEY,
  REFUND_DATE_KEY,
  REFUND_KEY,
  RENEWAL_RATE_KEY,
  SPLIT_KEY,
  STATUS_KEY,
  SUPPORTING_DUE_DATE_KEY,
  VALUATION_DATE_KEY,
  VALUATION_FEE_KEY,
} from 'common/constants/analyst-dashboards';
import { EMPTY_VALUATIONS_TABLE } from 'common/constants/documents';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';
import InlineNoteEditor from 'dashboard409a/components/ValuationsTable/common/InlineNoteEditor';
import CompanyNameCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/CompanyNameCell';
import ConsultantCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/ConsultantCell';
import DealCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/DealCell';
import DealDueCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/DealDueCell';
import DueCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/DueCell';
import EngagementManagerCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/EngagementManagerCell';
import FinancialFieldCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/FinancialFieldCell';
import { DealStageOption, EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import WorkboardDealGroupHeader from 'dashboard409a/components/WorkboardSpreadsheet/WorkboardDealGroupHeader';
import WorkboardDealStageCell from 'dashboard409a/components/WorkboardSpreadsheet/WorkboardDealStageCell';
import WorkboardSortHeader from 'dashboard409a/components/WorkboardSpreadsheet/WorkboardSortHeader';
import WorkboardStatusCell from 'dashboard409a/components/WorkboardSpreadsheet/WorkboardStatusCell';
import { valuationsColumns as allColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { AttioStatusOption, DealGroup, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import { alphabetGenerator } from 'utilities/alphabet-utilities';

const HEADER_ALIAS = '__workboard_header__';
const EMPTY_ALIAS = '__workboard_empty__';
const DEAL_GROUP_ALIAS_PREFIX = '__deal_group__';
const NO_DEAL_KEY = '__no_deal__';
const SHEET_NAME = 'workboard';

const INTERACTIVE_COLUMN_IDS = new Set([
  STATUS_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  ENGAGEMENT_MANAGER_KEY,
  DEAL_STAGE_KEY,
]);

const getDealGroupKey = (group: DealGroup) => group.deal_id ?? NO_DEAL_KEY;

interface UseWorkboardSheetParams {
  valuations: SimplifiedAttioValuation[];
  visibleColumnIds: string[];
  statusOptions: AttioStatusOption[];
  statusOptionsLoading: boolean;
  dealStageOptions: DealStageOption[];
  isNoteLoading?: boolean;
  sortCriteria?: SortCriteria[];
  dealGroups?: DealGroup[];
  collapsedDealIds?: string[];
  onToggleCollapsedDeal?: (dealKey: string) => void;
  onRequestSort?: (event: MouseEvent<unknown>, property: string, addToSort?: boolean) => void;
  onStatusUpdate: (row: SimplifiedAttioValuation) => void;
  onNoteSave: (valuationId: string, noteContent: string, currentNote?: string) => Promise<void>;
  onEngagementManagerUpdate: (recordId: string, option: EngagementManagerOption) => Promise<void>;
  onOtherConsultantsUpdate: (
    recordId: string,
    options: { targetRecordId: string; name: string; imageSrc: string }[]
  ) => Promise<void>;
  onDealStageChange: (row: SimplifiedAttioValuation, dealStageId: string) => Promise<void>;
}

const useWorkboardSheet = ({
  valuations,
  visibleColumnIds,
  statusOptions,
  statusOptionsLoading,
  dealStageOptions,
  isNoteLoading = false,
  sortCriteria,
  dealGroups,
  collapsedDealIds,
  onToggleCollapsedDeal,
  onRequestSort,
  onStatusUpdate,
  onNoteSave,
  onEngagementManagerUpdate,
  onOtherConsultantsUpdate,
  onDealStageChange,
}: UseWorkboardSheetParams) => {
  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  const spreadsheet = useMemo(() => {
    const labelMap = Object.fromEntries(allColumns.map(c => [c.id, c.label]));
    const sortableMap = Object.fromEntries(allColumns.map(c => [c.id, c.isSortable !== false]));

    const columns = visibleColumnIds.map(id => ({ id, label: labelMap[id] ?? id }));

    const rowConfig: { alias: string; gridType: 'string'; readOnly: true }[] = [
      { alias: HEADER_ALIAS, gridType: 'string', readOnly: true },
    ];
    const dealGroupMeta = new Map<
      string,
      { dealKey: string; dealName: string; valuationCount: number; isCollapsed: boolean }
    >();

    if (dealGroups && dealGroups.length > 0) {
      const collapsedSet = new Set(collapsedDealIds ?? []);
      dealGroups.forEach(group => {
        const dealKey = getDealGroupKey(group);
        if (group.valuations.length > 1) {
          const alias = `${DEAL_GROUP_ALIAS_PREFIX}${dealKey}`;
          const isCollapsed = collapsedSet.has(dealKey);
          rowConfig.push({ alias, gridType: 'string', readOnly: true });
          dealGroupMeta.set(alias, {
            dealKey,
            dealName: group.deal_name ?? 'No deal',
            valuationCount: group.valuations.length,
            isCollapsed,
          });
          if (!isCollapsed) {
            group.valuations.forEach(valuation => {
              rowConfig.push({ alias: valuation.record_id, gridType: 'string', readOnly: true });
            });
          }
          return;
        }
        if (group.valuations[0]) {
          rowConfig.push({ alias: group.valuations[0].record_id, gridType: 'string', readOnly: true });
        }
      });
    } else {
      valuations.forEach(valuation => {
        rowConfig.push({ alias: valuation.record_id, gridType: 'string', readOnly: true });
      });
    }

    // Header-only sheet looks blank; keep the old empty-table message as an explicit row.
    if (rowConfig.length === 1) {
      rowConfig.push({ alias: EMPTY_ALIAS, gridType: 'string', readOnly: true });
    }

    const valuationMap = new Map(valuations.map(v => [v.record_id, v]));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parser = ({ columns: cols, rowConfig: rows }: any) => {
      const cells: Record<string, any> = {};
      const alphabet = alphabetGenerator([], cols.length);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rows.forEach((row: any, rowIndex: number) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cols.forEach((column: any, colIndex: number) => {
          const key = `${alphabet[colIndex]}${rowIndex + 1}`;

          if (row.alias === HEADER_ALIAS) {
            const isSortable = Boolean(onRequestSort) && sortableMap[column.id] !== false;
            const currentSort = sortCriteria?.find(c => c.property === column.id);
            const sortDirection = currentSort?.direction ?? null;

            cells[key] = {
              readOnly: true,
              gridType: 'string',
              columnRef: column.id,
              value: column.label,
              ...(column.id === COMPANY_NAME_KEY ? { style: { textAlign: 'left' } } : {}),
              ...(isSortable
                ? {
                  forceComponent: true,
                  component: React.createElement(WorkboardSortHeader, {
                    title: column.label,
                    direction: sortDirection,
                    onClick: (event: { shiftKey: boolean }) => {
                      onRequestSort?.(event as MouseEvent<unknown>, column.id, event.shiftKey);
                    },
                  }),
                }
                : {}),
            };
            return;
          }

          if (row.alias === EMPTY_ALIAS) {
            cells[key] = {
              readOnly: true,
              gridType: 'string',
              columnRef: column.id,
              className: 'workboard-empty-message-cell',
              value: colIndex === 0 ? EMPTY_VALUATIONS_TABLE : '',
              ...(colIndex === 0 ? { style: { textAlign: 'left' } } : {}),
            };
            return;
          }

          const groupMeta = dealGroupMeta.get(row.alias);
          if (groupMeta) {
            if (colIndex === 0) {
              cells[key] = {
                readOnly: true,
                gridType: 'string',
                columnRef: column.id,
                value: groupMeta.dealName,
                className: 'workboard-deal-group-cell',
                style: { textAlign: 'left' },
                forceComponent: true,
                component: React.createElement(WorkboardDealGroupHeader, {
                  dealName: groupMeta.dealName,
                  valuationCount: groupMeta.valuationCount,
                  isCollapsed: groupMeta.isCollapsed,
                  onToggle: () => onToggleCollapsedDeal?.(groupMeta.dealKey),
                }),
              };
            } else {
              cells[key] = {
                readOnly: true,
                gridType: 'string',
                columnRef: column.id,
                value: '',
                className: 'workboard-deal-group-cell',
              };
            }
            return;
          }

          const valuation = valuationMap.get(row.alias);
          if (!valuation) {
            cells[key] = { readOnly: true, value: '-', gridType: 'string', columnRef: column.id };
            return;
          }

          const isInteractive = INTERACTIVE_COLUMN_IDS.has(column.id);
          const baseCell = {
            readOnly: true as const,
            gridType: 'string' as const,
            columnRef: column.id,
            ...(isInteractive ? { className: 'workboard-interactive-cell' } : { className: 'workboard-row-click-cell' }),
          };

          switch (column.id) {
            case COMPANY_NAME_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(CompanyNameCell, { valuationRow: valuation }),
                style: { textAlign: 'left' },
              };
              break;

            case STATUS_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(WorkboardStatusCell, {
                  valuationRow: valuation,
                  statusOptions,
                  statusOptionsLoading,
                  onStatusUpdate
                }),
                style: { textAlign: 'left' },
              };
              break;

            case NOTES_KEY: {
              const noteAuthorName =
                valuation.note?.author_id != null && valuation.note.author_id !== currentUserId
                  ? valuation.note.author_name || null
                  : null;
              cells[key] = {
                ...baseCell,
                className: `${baseCell.className} workboard-notes-cell`,
                forceComponent: true,
                // Empty string keeps expr eval safe; do not put note text in `value` (formula path).
                value: '',
                ignoreExpr: true,
                style: { textAlign: 'left' },
                component: React.createElement(InlineNoteEditor, {
                  key: String(valuation.record_id),
                  // Use noteContent — Cell clones children and overwrites the `value` prop.
                  noteContent: valuation.note?.content,
                  isLoading: isNoteLoading,
                  noteAuthorName,
                  onSave: async (savedContent: string) => {
                    await onNoteSave(valuation.record_id, savedContent, valuation.note?.content || '');
                  },
                }),
              };
              break;
            }

            case OTHER_CONSULTANT_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(ConsultantCell, {
                  key: valuation.record_id,
                  valuationRow: valuation,
                  onOtherConsultantsUpdate
                }),
              };
              break;

            case ENGAGEMENT_MANAGER_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(EngagementManagerCell, {
                  key: valuation.record_id,
                  valuationRow: valuation,
                  onEngagementManagerUpdate
                }),
              };
              break;

            case DEAL_STAGE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(WorkboardDealStageCell, {
                  valuationRow: valuation,
                  dealStageOptions,
                  onDealStageChange
                }),
              };
              break;

            case DEAL_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(DealCell, { valuationRow: valuation }),
              };
              break;

            case DEAL_DUE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(DealDueCell, { valuationRow: valuation }),
              };
              break;

            case DUE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(DueCell, { valuationRow: valuation }),
              };
              break;

            case DEAL_NAME_KEY:
              cells[key] = {
                ...baseCell,
                value: valuation.deal || '-',
              };
              break;

            case VALUATION_DATE_KEY:
              cells[key] = {
                ...baseCell,
                value: valuation.valuation_date || '-',
              };
              break;

            case CREATED_AT_KEY:
              cells[key] = {
                ...baseCell,
                value: valuation.created_at ? valuation.created_at.split('T')[0] : '-',
              };
              break;

            case VALUATION_FEE_KEY:
              cells[key] = {
                ...baseCell,
                value: valuation.valuation_fee || '-',
              };
              break;

            case SUPPORTING_DUE_DATE_KEY:
              cells[key] = {
                ...baseCell,
                value: valuation.supporting_due_date || '-',
              };
              break;

            case NET_VALUE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'net_value' }),
              };
              break;

            case SPLIT_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'split' }),
              };
              break;

            case PAYMENT_1_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'payment_1' }),
              };
              break;

            case PAY_1_DATE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'pay_1_date' }),
              };
              break;

            case PAYMENT_2_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'payment_2' }),
              };
              break;

            case PAY_2_DATE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'pay_2_date' }),
              };
              break;

            case PAYMENT_3_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'payment_3' }),
              };
              break;

            case PAY_3_DATE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'pay_3_date' }),
              };
              break;

            case REFUND_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'refund' }),
              };
              break;

            case REFUND_DATE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'refund_date' }),
              };
              break;

            case ADJUSTMENT_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'adjustment' }),
              };
              break;

            case ADJUSTMENT_DATE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'adjustment_date' }),
              };
              break;

            case BAD_DEBT_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'bad_debt' }),
              };
              break;

            case BAD_DEBT_DATE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'bad_debt_date' }),
              };
              break;

            case RENEWAL_RATE_KEY:
              cells[key] = {
                ...baseCell,
                forceComponent: true,
                component: React.createElement(FinancialFieldCell, { valuationRow: valuation, fieldKey: 'renewal_rate' }),
              };
              break;

            default:
              cells[key] = { ...baseCell, value: '-' };
          }
        });
      });

      return cells;
    };

    const config = {
      name: SHEET_NAME,
      columns,
      rowConfig,
      tableData: {},
      parser,
      afterCellChanged: (changes: any[]) => changes,
      reverseParser: undefined,
      isReadOnly: true,
      showTitlesColumn: false,
      showTotalColumn: false,
      showToolbar: false,
      displayRowIndicator: false,
      displayColumnIndicator: false,
      hasColTitle: true,
      alwaysDisplayLegend: false,
      ignoreCellExpressionErrors: true,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new SpreadsheetConfig(config as any);
  }, [
    valuations,
    visibleColumnIds,
    statusOptions,
    statusOptionsLoading,
    dealStageOptions,
    isNoteLoading,
    sortCriteria,
    dealGroups,
    collapsedDealIds,
    onToggleCollapsedDeal,
    onRequestSort,
    onStatusUpdate,
    onNoteSave,
    onEngagementManagerUpdate,
    onOtherConsultantsUpdate,
    onDealStageChange,
    currentUserId,
  ]);

  return { spreadsheet };
};

export default useWorkboardSheet;
